import React, {FC, ReactNode, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isInteger} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {DateCell} from '../../../components/common/table/cells/dateCell';
import {NumberInputCell} from '../../../components/common/table/cells/numberInputCell';
import {TableCellProps} from '../../../components/common/table/cells/tableCell';
import {ToggleCell} from '../../../components/common/table/cells/toggleCell';
import {TableColumn} from '../../../components/common/table/tableColumn';
import {TableRow, TableRowProps, TableRowRenderOptions} from '../../../components/common/table/tableRow';
import {RuleBundleTypesEnum} from '../../../models/api/ruleCommonTypes';
import {RequiredTaggingEditorActiveTooltip} from '../ruleEditor/recipes/requiredTagging/requiredTaggingEditorActiveTooltip';
import {RuleListItem, RuleListTableColumnsEnum} from './ruleListHelpers';
import {RuleNameCell} from './ruleNameCell';

/*
 * Props.
 */

export interface RuleListRowProps extends Omit<TableRowProps, 'renderColumn'>, RuleListRowActions {
  rule: RuleListItem;
  columns: ReadonlyArray<TableColumn<RuleListTableColumnsEnum>>;
  options: TableRowRenderOptions;
  shouldRemoveTopBorderRadius: boolean;
  shouldRemoveBottomBorderRadius: boolean;
  isInMultiSelectMode: boolean;
}

export interface RuleListRowActions {
  onRuleEdit: HandlerOf<RuleListItem>;
  onRuleDelete: HandlerOf<RuleListItem>;
  onRuleReorder: (rule: RuleListItem, newOrder: number) => void;
  onRuleToggleActive: (rules: ReadonlyArray<RuleListItem>) => void;
}

/*
 * Styles.
 */

const StyledTableRow = styled(TableRow)`
  min-height: 56px;
`;

/*
 * Component.
 */

export const RuleListRow: FC<RuleListRowProps> = ({
  rule,
  columns,
  options,
  onRuleEdit,
  onRuleDelete,
  onRuleReorder,
  onRuleToggleActive,
  shouldRemoveBottomBorderRadius,
  shouldRemoveTopBorderRadius,
  ...rowProps
}) => {
  /**
   * localSortOrder store the order while the user is typing. We update the rule
   * order when the user press Enter.
   * This avoids updating the position of the rule everytime (and make an API call)
   * the user is typing.
   */
  const [localSortOrder, setLocalSortOrder] = useState<number | undefined>(rule.order);

  useEffect(() => {
    setLocalSortOrder(rule.order);
  }, [rule.order]);

  return (
    <StyledTableRow
      {...rowProps}
      hasHover
      onSelect={(event) => {
        if (event.defaultPrevented) {
          return;
        }

        event.preventDefault();

        if (rowProps.isInMultiSelectMode) {
          rowProps.onCheckBoxClick?.(event);
          return;
        }

        onRuleEdit(rule);
      }}
      onCheckBoxClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }

        event.preventDefault();
        rowProps.onCheckBoxClick?.(event);
      }}
      renderColumn={(isHovering) =>
        columns.map((column, index) =>
          COLUMN_RENDERERS[column.type](
            rule,
            {
              ...options,
              index,
              key: `${column.type}-${rule.id}`,
              isHovering,
              isSelected: rowProps.isSelected,
              settings: column.settings,
              shouldRemoveBottomBorderRadius,
              shouldRemoveTopBorderRadius,
              localOrder: localSortOrder,
              isInMultiSelectMode: rowProps.isInMultiSelectMode,
            },
            {onRuleEdit, onRuleDelete, onRuleReorder, onRuleToggleActive, onLocalReorder: setLocalSortOrder},
          ),
        )
      }
      shouldShowRightGradient={options.shouldShowRightGradient}
    />
  );
};

/*
 * Helpers.
 */

interface InheritableRuleCellProps extends TableCellProps<RuleListItem> {
  key: string;
  localOrder: number | undefined;
  isInMultiSelectMode: boolean;
}
interface RuleListRowActionsWithReorder extends RuleListRowActions {
  onLocalReorder: HandlerOf<number | undefined>;
}

type CellRenderer = (
  rule: RuleListItem,
  cellProps: InheritableRuleCellProps,
  actions: RuleListRowActionsWithReorder,
) => ReactNode;

const COLUMN_RENDERERS: Record<RuleListTableColumnsEnum, CellRenderer> = {
  [RuleListTableColumnsEnum.NAME]: (rule, cellProps, {onRuleDelete, onRuleEdit}) => (
    <RuleNameCell {...cellProps} rule={rule} onRuleDelete={onRuleDelete} onRuleEdit={onRuleEdit} />
  ),
  [RuleListTableColumnsEnum.ORDER]: (rule, cellProps, {onRuleReorder, onLocalReorder}) => (
    <NumberInputCell
      {...cellProps}
      onIncrement={(event) => {
        event.preventDefault();
        if (rule.order) {
          onRuleReorder(rule, rule.order - 1);
        }
      }}
      onDecrement={(event) => {
        event.preventDefault();
        if (rule.order) {
          onRuleReorder(rule, rule.order + 1);
        }
      }}
      onChange={(value) => {
        if (isInteger(value)) {
          onLocalReorder(value);
        } else {
          onLocalReorder(undefined);
        }
      }}
      value={cellProps.localOrder}
      onBlur={() => {
        onLocalReorder(rule.order);
      }}
      onKeyDown={(event) => {
        if (event.key === Key.Enter && cellProps.localOrder !== undefined) {
          onRuleReorder(rule, cellProps.localOrder);
        }
      }}
      disableIncrement={rule.order <= 1}
      isInMultiSelectMode={cellProps.isInMultiSelectMode}
    />
  ),
  [RuleListTableColumnsEnum.ACTIVE]: (rule, cellProps, {onRuleToggleActive}) => (
    <ToggleCell
      {...cellProps}
      key={rule.id}
      value={rule.isActive}
      // TODO @required-tagging: Allow disable/enable.
      isDisabled={rule.bundleType === RuleBundleTypesEnum.REQUIRED_TAGGING ? true : undefined}
      onChange={() => onRuleToggleActive([rule])}
      renderToolip={getToggleTooltipRenderer(rule)}
    />
  ),
  [RuleListTableColumnsEnum.LAST_APPLIED]: (rule, cellProps) => (
    <DateCell
      {...cellProps}
      customFormat={{
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }}
      value={rule.lastApplied}
    />
  ),
};

/*
 * Helpers.
 */

function getToggleTooltipRenderer(rule: RuleListItem) {
  switch (rule.bundleType) {
    case RuleBundleTypesEnum.REQUIRED_TAGGING:
      return renderToggleRequiredTaggingTooltip;

    default:
      return undefined;
  }
}

function renderToggleRequiredTaggingTooltip() {
  return <RequiredTaggingEditorActiveTooltip />;
}
